/**
 * Shopping 페이지 카테고리·상품 정적 다국어 매핑.
 *
 * 배경: 백엔드/더미 API가 반환하는 category·name이 한국어라서 useDeepLTranslated 런타임
 *       번역에 의존. DeepL 키 미설정·쿼터 초과·지연 시 한국어 그대로 노출되는 문제 발생.
 *
 * 여기에 엔트리가 있으면 DeepL 호출 없이 즉시 치환. 없으면 useDeepLTranslated가 기존대로 처리.
 * 실제 운영에선 백엔드 i18n 필드(name_en, name_ja 등)로 이관 권장.
 */

// BugHerd #79 follow-up: 한국어 ko 매핑 추가. BE 가 category code(oliveyoung/daiso/mart/other)
// 를 raw 로 내려주는데 기존엔 ko 가 매핑 미대상이라 영어 코드가 한국어 모드에서도 그대로 노출.
const CATEGORY_MAP = {
  '올리브영': { ko: '올리브영', en: 'Olive Young', ja: 'オリーブヤング', zh: 'Olive Young', es: 'Olive Young' },
  '다이소': { ko: '다이소', en: 'Daiso', ja: 'ダイソー', zh: 'Daiso', es: 'Daiso' },
  '마트': { ko: '마트', en: 'Supermarket', ja: 'スーパー', zh: '超市', es: 'Supermercado' },
  '기타': { ko: '기타', en: 'Others', ja: 'その他', zh: '其他', es: 'Otros' },
  'oliveyoung': { ko: '올리브영', en: 'Olive Young', ja: 'オリーブヤング', zh: 'Olive Young', es: 'Olive Young' },
  'daiso': { ko: '다이소', en: 'Daiso', ja: 'ダイソー', zh: 'Daiso', es: 'Daiso' },
  'mart': { ko: '마트', en: 'Supermarket', ja: 'スーパー', zh: '超市', es: 'Supermercado' },
  'other': { ko: '기타', en: 'Others', ja: 'その他', zh: '其他', es: 'Otros' },
};

const PRODUCT_MAP = {
  // 올리브영 (K-beauty)
  '라운드랩 자작나무 수분크림': {
    en: 'Round Lab Birch Moisturizing Cream',
    ja: 'ラウンドラボ 白樺 保湿クリーム',
    zh: 'Round Lab 白桦树保湿霜',
    es: 'Round Lab Crema Hidratante de Abedul',
  },
  '토리든 다이브인 세럼': {
    en: 'Torriden Dive-In Serum',
    ja: 'トリデン ダイブイン セラム',
    zh: 'Torriden Dive-In 精华液',
    es: 'Torriden Sérum Dive-In',
  },
  '메디힐 티트리 마스크팩 10매': {
    en: 'Mediheal Tea Tree Mask Pack (10 sheets)',
    ja: 'メディヒール ティーツリー マスクパック 10枚',
    zh: 'Mediheal 茶树面膜 10片装',
    es: 'Mediheal Mascarilla Árbol de Té (10 uds)',
  },
  '이니스프리 그린티 세트': {
    en: 'Innisfree Green Tea Set',
    ja: 'イニスフリー グリーンティー セット',
    zh: 'Innisfree 绿茶套装',
    es: 'Innisfree Set Té Verde',
  },
  '조선미녀 맑은쌀 선크림': {
    en: 'Beauty of Joseon Relief Sun Rice Sunscreen',
    ja: 'チョウセンビジョ 米 サンクリーム',
    zh: 'Beauty of Joseon 米粹防晒霜',
    es: 'Beauty of Joseon Protector Solar de Arroz',
  },
  // 다이소
  '미니 수납함 세트': {
    en: 'Mini Storage Box Set',
    ja: 'ミニ収納ボックスセット',
    zh: '迷你收纳盒套装',
    es: 'Set de Cajas de Almacenamiento Mini',
  },
  '감성 마스킹테이프 5종': {
    en: 'Aesthetic Masking Tape (5 types)',
    ja: '感性マスキングテープ 5種',
    zh: '文艺风遮蔽胶带 5种',
    es: 'Cinta Washi Decorativa (5 tipos)',
  },
  '접이식 에코백': {
    en: 'Foldable Eco Bag',
    ja: '折りたたみエコバッグ',
    zh: '折叠环保袋',
    es: 'Bolsa Ecológica Plegable',
  },
  // 마트
  '신라면 멀티팩 5입': {
    en: 'Shin Ramyun Multipack (5 packs)',
    ja: '辛ラーメン マルチパック 5個入',
    zh: '辛拉面 5袋装',
    es: 'Shin Ramyun Pack de 5',
  },
  '비비고 만두 350g': {
    en: 'Bibigo Dumplings 350g',
    ja: 'ビビゴ マンドゥ 350g',
    zh: 'Bibigo 饺子 350g',
    es: 'Empanadillas Bibigo 350g',
  },
  '오리온 초코파이 12개입': {
    en: 'Orion Choco Pie (12 pcs)',
    ja: 'オリオン チョコパイ 12個入',
    zh: '好丽友 巧克力派 12个装',
    es: 'Orion Choco Pie (12 uds)',
  },
  // 기타
  '한국 전통 기념품 세트': {
    en: 'Korean Traditional Souvenir Set',
    ja: '韓国伝統土産セット',
    zh: '韩国传统纪念品套装',
    es: 'Set de Recuerdos Tradicionales Coreanos',
  },
  // 투어 패키지명 (더미 shopping packages)
  '프라이빗 쇼핑 - 명품 아울렛 투어': {
    en: 'Private Shopping — Luxury Outlet Tour',
    ja: 'プライベートショッピング - ラグジュアリーアウトレットツアー',
    zh: '私人购物 - 奢侈品奥莱之旅',
    es: 'Compras Privadas — Tour a Outlets de Lujo',
  },
  '프라이빗 쇼핑 - 강남 럭셔리 투어': {
    en: 'Private Shopping — Gangnam Luxury Tour',
    ja: 'プライベートショッピング - 江南ラグジュアリーツアー',
    zh: '私人购物 - 江南奢华之旅',
    es: 'Compras Privadas — Tour de Lujo en Gangnam',
  },
  '올리브영 뷰티 쇼핑 투어': {
    en: 'Olive Young K-Beauty Shopping Tour',
    ja: 'オリーブヤング ビューティーショッピングツアー',
    zh: 'Olive Young 美妆购物之旅',
    es: 'Tour de Compras K-Beauty Olive Young',
  },
  '대형마트 식품 쇼핑 투어': {
    en: 'Supermarket Food Shopping Tour',
    ja: '大型マート フード ショッピングツアー',
    zh: '大型超市美食购物之旅',
    es: 'Tour de Compras en Supermercado',
  },
  '다이소 생활용품 쇼핑 투어': {
    en: 'Daiso Household Goods Shopping Tour',
    ja: 'ダイソー 生活用品 ショッピングツアー',
    zh: 'Daiso 生活用品购物之旅',
    es: 'Tour de Compras en Daiso',
  },
};

export function translateCategoryName(koName, beLang) {
  // BugHerd #79 follow-up: ko 도 매핑 대상 — BE 가 category code(oliveyoung 등) 를 그대로
  // 내려주는 케이스에서 한국어 모드에서도 한글 라벨 노출 보장.
  if (!koName) return koName;
  const lang = beLang || 'ko';
  return CATEGORY_MAP[koName]?.[lang] || koName;
}

export function translateProductName(koName, beLang) {
  if (!koName || !beLang || beLang === 'ko') return koName;
  return PRODUCT_MAP[koName]?.[beLang] || koName;
}

export { CATEGORY_MAP, PRODUCT_MAP };
